import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import flatpickr from "flatpickr";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import "flatpickr/dist/flatpickr.min.css";
import { Japanese } from "flatpickr/dist/l10n/ja";
import { format } from "date-fns";
import { ja } from "date-fns/locale";

import CalendarIcon from "assets/icons/CalendarIcon";

interface DateInputProps {
  name: string;
  currentDate: Date;
  maxDate?: Date;
  minDate?: Date;
  enableTime?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  error?: string;
  label?: string;
  required?: boolean;
  onChange: (date: Date) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const DateInput: React.FC<DateInputProps> = ({
  name,
  currentDate,
  maxDate,
  minDate,
  enableTime = false,
  disabled = false,
  placeholder,
  className = "",
  error,
  label,
  required = false,
  onChange,
  onFocus,
  onBlur,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const flatpickrRef = useRef<FlatpickrInstance | null>(null);

  const [internalDate, setInternalDate] = useState<Date>(currentDate);
  const [isFocused, setIsFocused] = useState(false);

  const flatpickrConfig = useMemo(
    () => ({
      locale: Japanese,
      enableTime,
      dateFormat: enableTime ? "Y年n月j日 H:i" : "Y年n月j日",
      maxDate,
      minDate,
      disableMobile: true,
      allowInput: false,
      clickOpens: !disabled,
      defaultDate: currentDate,
      onOpen: () => {
        setIsFocused(true);
        onFocus?.();
      },
      onClose: () => {
        setIsFocused(false);
        onBlur?.();
      },
      onChange: (selectedDates: Date[]) => {
        if (selectedDates.length === 0) return;

        const selectedDate = new Date(selectedDates[0]);
        if (!enableTime) {
          selectedDate.setHours(0, 0, 0, 0);
        }

        setInternalDate(selectedDate);
        onChange(selectedDate);
      },
    }),
    [
      enableTime,
      maxDate,
      minDate,
      disabled,
      currentDate,
      onChange,
      onFocus,
      onBlur,
    ]
  );

  const handleWrapperClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();
      flatpickrRef.current?.open();
    },
    [disabled]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        flatpickrRef.current?.open();
      }
    },
    [disabled]
  );

  useEffect(() => {
    if (!inputRef.current) return;

    if (flatpickrRef.current) {
      flatpickrRef.current.destroy();
    }

    flatpickrRef.current = flatpickr(inputRef.current, flatpickrConfig);

    return () => {
      if (flatpickrRef.current) {
        flatpickrRef.current.destroy();
        flatpickrRef.current = null;
      }
    };
  }, [flatpickrConfig]);

  useEffect(() => {
    if (currentDate.getTime() !== internalDate.getTime()) {
      setInternalDate(currentDate);
      if (flatpickrRef.current) {
        flatpickrRef.current.setDate(currentDate, false);
      }
    }
  }, [currentDate, internalDate]);

  const displayValue = useMemo(() => {
    try {
      return enableTime
        ? format(internalDate, "yyyy年M月d日 HH:mm", { locale: ja })
        : format(internalDate, "yyyy年M月d日", { locale: ja });
    } catch (error) {
      console.error("Date formatting error:", error);
      return "Invalid Date";
    }
  }, [internalDate, enableTime]);

  const wrapperClasses = useMemo(() => {
    const baseClasses = [
      "w-full",
      "p-3",
      "rounded-lg",
      "bg-white",
      "flex",
      "flex-row",
      "items-center",
      "border",
      "transition-colors",
      "duration-200",
    ];

    if (disabled) {
      baseClasses.push("cursor-not-allowed", "opacity-60", "border-gray-300");
    } else {
      baseClasses.push("cursor-pointer");

      if (error) {
        baseClasses.push("border-red-500", "focus-within:border-red-600");
      } else if (isFocused) {
        baseClasses.push(
          "border-blue-500",
          "ring-1",
          "ring-blue-500",
          "ring-opacity-50"
        );
      } else {
        baseClasses.push("border-gray-400", "hover:border-gray-500");
      }
    }

    return baseClasses.join(" ");
  }, [disabled, error, isFocused]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div
          ref={wrapperRef}
          className={wrapperClasses}
          onClick={handleWrapperClick}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-label={`Date picker for ${label || name}`}
          aria-expanded={isFocused}
          aria-haspopup="dialog"
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        >
          <input
            ref={inputRef}
            className="w-full text-base outline-none cursor-pointer bg-transparent text-gray-900 placeholder-gray-500"
            type="text"
            name={name}
            id={name}
            readOnly
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            tabIndex={-1}
            aria-hidden="true"
          />

          <div className="flex-shrink-0 ml-2">
            <CalendarIcon
              width={20}
              height={20}
              color={disabled ? "#9CA3AF" : "#6B7280"}
            />
          </div>
        </div>

        {disabled && (
          <div
            className="absolute inset-0 bg-white bg-opacity-50 rounded-lg pointer-events-none"
            aria-hidden="true"
          />
        )}
      </div>

      {error && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default DateInput;
